import { supabase } from "../../lib/supabase";
import { fetchAllChats, normalizePhoneToJid } from "./sync";
import { fetchAllMessagesGlobal } from "./history/globalMessagesFetcher";
import { invalidateEnrichmentCache } from "../repositories/helpers/ConversationEnrichmentHelper";

// Use cases extraídos (Clean Architecture / Single Responsibility)
import { getOwnerJid } from "./history/historyFetchers";
import { processChatHistory } from "./history/historyProcessor";

// Re-export dos services para manter compatibilidade com módulos antigos
export * from "./history/historyFetchers";

interface SyncHistoryOptions {
  maxChats?: number;
  messagesPerChat?: number;
  onProgress?: (current: number, total: number) => void;
}

/**
 * syncHistory - Único ponto de entrada de sincronização do Histórico Global.
 *
 * Responsabilidades:
 * 1. Orquestrar a busca do integration_user e ownerJid
 * 2. Orquestrar chamadas pesadas (`fetchChats`, `fetchAllMessagesGlobal`)
 * 3. Delegar o processamento isolado de cada chat ao `processChatHistory` (SRP).
 */
export const syncHistory = async (
  instanceName: string,
  connectionId: string,
  organizationId: string,
  options?: SyncHistoryOptions,
) => {
  const maxChats = options?.maxChats || 500;
  const messagesPerChat = options?.messagesPerChat || 2000;
  const onProgress = options?.onProgress;

  let chatsImported = 0;
  let messagesImported = 0;
  let errors = 0;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Usuário não autenticado. Faça login novamente.');
    }

    const ownerJid = await getOwnerJid(instanceName);
    console.log(`[History Sync] 🔑 Owner JID: ${ownerJid}`);

    const { data: _integrationUser } = await supabase
      .from("integrations")
      .select("user_id")
      .eq("id", connectionId)
      .single();
    const integrationUserId = _integrationUser?.user_id;

    // 1. Buscar todos os chats
    const chats = await fetchAllChats(
      instanceName,
      Math.ceil(maxChats / 50),
      50,
      ownerJid,
    );
    
    // ORDENAR DO MAIS NOVO PARA O MAIS ANTIGO ANTES DE CORTAR
    chats.sort((a: any, b: any) => {
      const tsA = a.wa_lastMsgTimestamp || a.lastMsgTimestamp || a.wa_t || a.timestamp || 0;
      const tsB = b.wa_lastMsgTimestamp || b.lastMsgTimestamp || b.wa_t || b.timestamp || 0;
      return Number(tsB) - Number(tsA);
    });

    const chatsToSync = chats.slice(0, maxChats);
    console.log(`[History Sync] ${chatsToSync.length} chats para sincronizar (mais novos primeiro)`);

    // 2. OTIMIZAÇÃO: Buscar TODAS as mensagens UMA VEZ e distribuir
    const globalMsgMap = await fetchAllMessagesGlobal(instanceName);

    // 3. Processar cada chat
    for (let i = 0; i < chatsToSync.length; i++) {
      const chat = chatsToSync[i];
      onProgress?.(i + 1, chatsToSync.length);

      try {
        const rawChat = chat as any;
        const remoteJid = rawChat.wa_chatid || normalizePhoneToJid(chat.phone);

        if (
          !remoteJid ||
          remoteJid.includes("@lid") ||
          remoteJid.includes("@g.us")
        ) {
          continue;
        }

        const chatMessages = globalMsgMap.get(remoteJid) || [];
        const lastMsgTextPreCheck =
          rawChat.wa_lastMessageTextVote ||
          rawChat.wa_lastMessageBody ||
          rawChat.wa_lastMessageText ||
          "";
        const lastMsgTsPreCheck =
          rawChat.wa_lastMsgTimestamp ||
          rawChat.lastMsgTimestamp ||
          rawChat.wa_t ||
          rawChat.timestamp;

        if (
          !chatMessages.length &&
          (!lastMsgTextPreCheck || !lastMsgTsPreCheck)
        ) {
          continue;
        }

        // Delega o processamento daquele chat para a layer processChatHistory
        const result = await processChatHistory({
          chat,
          instanceName,
          connectionId,
          organizationId,
          integrationUserId,
          ownerJid,
          remoteJid,
          chatMessages,
          messagesPerChat,
        });

        if (result.hasError) {
          errors++;
          continue;
        }

        chatsImported++;
        messagesImported += result.insertedCount;

        if (result.insertedCount > 0 || (i + 1) % 50 === 0) {
          console.log(
            `[History Sync] ✅ Chat ${i + 1}/${chatsToSync.length}: ${rawChat.name || remoteJid} → ${result.insertedCount} novas msgs`,
          );
        }
      } catch (chatError: any) {
        console.warn(`[History Sync] ❌ Erro chat ${i}:`, chatError.message);
        errors++;
      }

      // Delay entre chats para não enfileirar muito o JS/V8
      if (i < chatsToSync.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }
  } catch (err: any) {
    console.error(`[History Sync] Erro geral:`, err.message);
    errors++;
  }

  console.log(
    `[History Sync] 🏁 Concluído: ${chatsImported} chats, ${messagesImported} mensagens, ${errors} erros`,
  );
  invalidateEnrichmentCache(organizationId);

  return { chatsImported, messagesImported, errors };
};
